<template>
    <WatsonThemeHeaderProductButton
        v-if="headerCtaBlock.type === 'button'"
        :price-guide="priceGuide"
        :cta-block="postTypes.priceGuideHeaderButtonCtaBlock"
        :cta-block-overwrite="headerCtaBlock"
    />

    <WatsonThemeHeaderProduct
        v-else
        :price-guide="priceGuide"
        :cta-block="postTypes.priceGuideHeaderCtaBlock"
        :cta-block-overwrite="headerCtaBlock"
    />

    <WatsonOrganismContentLayout>
        <template #content>
            <WatsonOrganismBreadcrumbs
                :items="[
                    {
                        label: 'Accueil',
                    },
                    ...breadCrumbs,
                    {
                        label: pageSettings.title || pageSettings.editorTitle,
                        url: page.props.url,
                    }
                ]"
            />

            <WatsonThemeArticleHeader
                :authors="authors"
                :heading-tag="'h2'"
                :disabled-image="disabledImage"
            />

            <Component
                :is="componentName(component)"
                v-for="(component, index) in content"
                :key="index"
                :content="component"
                v-bind="component.attrs.bindData"
            />

            <WatsonOrganismIconLinkBar
                v-if="iconLinkBar && !iconLinkBar.disable"
            />

            <WatsonThemeAuthorBlockPageEnd
                v-if="authors.writer"
                :content="authors"
            />

            <WatsonThemeLinkBlock v-if="!linkBlockState && linkBlockData.length > 0" />
        </template>

        <template #sidebar>
            <WatsonThemeUsps
                v-if="postTypes.usps"
                :content="postTypes.usps"
                v-bind="postTypes.usps"
            />

            <div>
                <WatsonOrganismJumpLinksVertical
                    v-if="!jumpLinksVertical.hide"
                    :hide="jumpLinksVertical.hide"
                    :title="jumpLinksVertical.title"
                    :collapsable="true"
                />

                <WatsonThemeCtaBlock
                    v-if="postTypes.sidebarCtaBlock"
                    v-bind="postTypes.sidebarCtaBlock"
                    :heading="sidebarCtaBlock.heading || postTypes.sidebarCtaBlock.heading"
                    :subheading="sidebarCtaBlock.subheading || postTypes.sidebarCtaBlock.subheading"
                    :button="{
                        ...postTypes.sidebarCtaBlock.button,
                        text: sidebarCtaBlock.buttonText || postTypes.sidebarCtaBlock.button.text,
                    }"
                    :tiny-text="sidebarCtaBlock.tinyText || postTypes.sidebarCtaBlock.tinyText"
                />

                <WatsonThemeListLogos
                    v-if="postTypes.listLogos"
                    v-bind="postTypes.listLogos"
                />
            </div>
        </template>
    </WatsonOrganismContentLayout>

    <WatsonThemeCtaBlock
        v-if="postTypes.priceGuideBottomCtaBlock"
        v-bind="postTypes.priceGuideBottomCtaBlock"
        :heading="bottomCtaBlock.heading || postTypes.priceGuideBottomCtaBlock.heading"
        :subheading="bottomCtaBlock.subheading || postTypes.priceGuideBottomCtaBlock.subheading"
        :button="{
            ...postTypes.priceGuideBottomCtaBlock.button,
            text: bottomCtaBlock.buttonText || postTypes.priceGuideBottomCtaBlock.button.text,
        }"
        :tiny-text="bottomCtaBlock.tinyText || postTypes.priceGuideBottomCtaBlock.tinyText"
    />
</template>

<script setup>
import { computed } from 'vue';
import { usePage } from '@inertiajs/vue3';

import componentName from '@utils/component-name';
import { useTableOfContents } from '../../../../js/stores/table-of-contents';

import WatsonOrganismContentLayout from '@organisms/content-layout/watson-organism-content-layout.vue';
import WatsonOrganismJumpLinksVertical from '@organisms/jump-links-vertical/watson-organism-jump-links-vertical.vue';

import WatsonThemeArticleHeader from '../../components/article-header/watson-theme-article-header.vue';
import WatsonThemeAuthorBlockPageEnd from '../../components/author-block-page-end/watson-theme-author-block-page-end.vue';
import WatsonThemeCtaBlock from '../../components/cta-block/watson-theme-cta-block.vue';
import WatsonThemeListLogos from '../../components/list-logos/watson-theme-list-logos.vue';
import WatsonThemeHeaderProduct from '../../components/header-product/watson-theme-header-product.vue';
import WatsonThemeLinkBlock from '../../components/link-block/watson-theme-link-block.vue';
import WatsonThemeUsps from '../../components/usps/watson-theme-usps.vue';
import WatsonOrganismBreadcrumbs from '@organisms/breadcrumbs/watson-organism-breadcrumbs.vue';
import WatsonOrganismIconLinkBar from '@organisms/icon-link-bar/watson-organism-icon-link-bar.vue';
import WatsonThemeHeaderProductButton from '../../components/header-product-button/watson-theme-header-product-button.vue';

const props = defineProps({
    category: {
        type: Object,
        required: false,
        default: null,
    },
    content: {
        type: Array,
        required: true,
    },
    pageSettings: {
        type: Object,
        required: true,
    },
    priceGuide: {
        type: Object,
        required: true,
    },
    authors: {
        type: Object,
        required: true,
    },
    postTypes: {
        type: Object,
        required: true,
    },
    headerCtaBlock: {
        type: Object,
        required: true,
    },
    bottomCtaBlock: {
        type: Object,
        required: true,
    },
    sidebarCtaBlock: {
        type: Object,
        required: true,
    },
    jumpLinksVertical: {
        type: Object,
        required: false,
        default: null,
    },
    linkBlock: {
        type: Object,
        required: false,
        default: null,
    },
    linkBlockData: {
        type: Array,
        required: false,
        default: () => [],
    },
    parents: {
        type: Array,
        required: false,
        default: () => [],
    },
});

const breadCrumbs = computed(() => {
    if (props.category && props.category.name && props.category.url) {
        return [
            {
                label: props.category.name,
                url: props.category.url,
            },
        ];
    }

    return props.parents.map(parent => ({
        label: parent.name,
        url: parent.url,
    }));
});

const disabledImage = computed(() => {
    const imageWidth = props.pageSettings?.featuredImage?.width || 0;
    return imageWidth <= 400;
});

const page = usePage();
const iconLinkBar = page.props.settings.globalComponents.iconLinkBar;

const tableOfContents = useTableOfContents(`table-of-contents-${page.props.url}`);
tableOfContents.enabled = props.jumpLinksVertical ? !props.jumpLinksVertical.hide : false;
tableOfContents.initializeHeadings(props.content);

const linkBlockState = props.linkBlock ? props.linkBlock.disabled : false;
</script>
